import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import ConfirmModal from '../../components/ConfirmModal';
import {api} from '../../api';


const STAGE_COLORS = {
  DISCOVERY: 'bg-purple-100 text-purple-800',
  QUALIFIED: 'bg-blue-100 text-blue-800',
  PROPOSAL: 'bg-yellow-100 text-yellow-800',
  NEGOTIATION: 'bg-orange-100 text-orange-800',
  CLOSED_WON: 'bg-green-100 text-green-800',
  CLOSED_LOST: 'bg-red-100 text-red-800',
};

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);

  useEffect(() => {
    api(`/companies/${id}`).then(r => r.json()).then(setCompany);
    api(`/contacts`).then(r => r.json()).then(all =>
      setContacts(all.filter(c => c.companies && c.companies.some(co => co.companyId === id)))
    ).catch(() => {});
    api(`/deals`).then(r => r.json()).then(all =>
      setDeals(all.filter(d => d.companyId === id))
    ).catch(() => {});
  }, [id]);

  const handleDelete = async () => {
    await api(`/companies/${id}`, { method: 'DELETE' });
    navigate('/crm/companies');
  };

  if (!company) return null;

  return (
    <Layout>
      <Link to="/crm/companies" className="text-blue-600 hover:text-blue-800 mb-4 inline-block">&larr; Back to Companies</Link>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{company.name}</h1>
              {company.industry && <p className="text-gray-600">{company.industry}</p>}
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                company.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {company.status}
              </span>
              {company.tags && company.tags.length > 0 && company.tags.map(t => (
                <span key={t.id} className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">{t.name}</span>
              ))}
              <Link to={`/crm/companies/${id}/edit`} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Edit</Link>
              <button onClick={() => setDeleteModal(true)} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-blue-50">Delete</button>
            </div>
          </div>
        </div>
        <div className="p-4 md:p-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-500 mb-1">Website</p>
            <p className="text-gray-900 font-medium">{company.website ? <a href={company.website.startsWith('http') ? company.website : `https://${company.website}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800">{company.website}</a> : '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Phone</p>
            <p className="text-gray-900 font-medium">{company.phone || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Address</p>
            <p className="text-gray-900 font-medium">{company.address || '—'}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 md:p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Contacts</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contacts.map((c) => {
                const role = c.companies && c.companies.find(co => co.companyId === id);
                return (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap font-medium">
                      <Link to={`/crm/contacts/${c.id}`} className="text-blue-600 hover:text-blue-800">{c.firstName} {c.lastName}</Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600 hidden md:table-cell">{(role && role.title) || '—'}</td>
                    <td className="px-6 py-4 text-gray-600 hidden md:table-cell text-sm">{(role && role.emails && role.emails.length > 0) ? role.emails.join(', ') : (c.emails && c.emails.length > 0 ? c.emails[0].email : '—')}</td>
                    <td className="px-6 py-4 text-gray-600 hidden md:table-cell text-sm">{(role && role.phones && role.phones.length > 0) ? role.phones.join(', ') : (c.phones && c.phones.length > 0 ? c.phones.map(p => p.phone).join(', ') : '—')}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${c.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{c.status}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {contacts.length === 0 && (
            <div className="text-center py-12 text-gray-500">No contacts associated with this company.</div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 md:p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Deals</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deal</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {deals.map((d) => (
                <tr key={d.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium">
                    <Link to={`/crm/deals/${d.id}`} className="text-blue-600 hover:text-blue-800">{d.name}</Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900 font-semibold">{d.value != null ? `$${d.value.toLocaleString()}` : '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${STAGE_COLORS[d.stage] || 'bg-gray-100 text-gray-800'}`}>{d.stage.replace(/_/g, ' ')}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {deals.length === 0 && (
            <div className="text-center py-12 text-gray-500">No deals associated with this company.</div>
          )}
        </div>
      </div>

      <ConfirmModal
        open={deleteModal}
        title="Delete Company"
        message="Are you sure you want to delete this company?"
        onConfirm={handleDelete}
        onCancel={() => setDeleteModal(false)}
      />
    </Layout>
  );
}
